import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Switch, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import {
  EVENT_CATEGORIES,
  EVENT_FORMATS,
  EVENT_LIMITS,
  EVENT_VISIBILITIES,
  formNeedsOnlineUrl,
  EVENT_PRICE_MAX_CENTS,
  EVENT_PRICE_MIN_CENTS,
  formNeedsVenue,
  formatBps,
  formatEventPrice,
  parsePriceInput,
  type EventFormState,
} from '@utils'
import { useScreen } from '@hooks'
import { CoverImageField } from './CoverImageField'
import { DateTimeField } from './DateTimeField'
import { FormSection } from './FormSection'
import { OptionChips } from './OptionChips'
import { VenueSearchField } from './VenueSearchField'

export interface SectionProps {
  values: EventFormState
  /** Already translated messages, by field. */
  errors: Partial<Record<keyof EventFormState, string>>
  onChange: <K extends keyof EventFormState>(key: K, value: EventFormState[K]) => void
  /** The organizer's KeepTouch fee, shown read-only (a super-admin sets it per organizer). */
  billing?: { commissionRateBps: number; feePaidBy: 'attendee' | 'organizer' } | null
}

const useSectionStyles = () => {
  const theme = useTheme()
  const { isMobile } = useScreen()
  const styles = getStyles(theme)
  return {
    styles,
    rowStyle: [styles.row, isMobile && styles.column],
    /** `flex` only makes sense side by side: in a column it would collapse the cells. */
    grow: (weight: 1 | 2 = 1) =>
      isMobile ? undefined : weight === 2 ? styles.flex2 : styles.flex1,
  }
}

export function InfoSection({ values, errors, onChange }: SectionProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const { styles } = useSectionStyles()
  return (
    <FormSection title={t('events.form.sections.info')}>
      <TextInput
        title={t('events.form.name')}
        placeholder={t('events.form.namePlaceholder')}
        value={values.name}
        onChangeText={(v) => onChange('name', v)}
        maxLength={EVENT_LIMITS.nameMax}
        errorMessage={errors.name}
        testID="event-name-input"
      />
      <OptionChips
        label={t('events.form.category')}
        testID="event-category"
        options={EVENT_CATEGORIES.map((value) => ({ value, label: t(`events.category.${value}`) }))}
        value={values.category}
        onChange={(v) => onChange('category', v)}
      />
      <View style={styles.field}>
        <TextInput
          title={t('events.form.description')}
          placeholder={t('events.form.descriptionPlaceholder')}
          value={values.description}
          onChangeText={(v) => onChange('description', v)}
          maxLength={EVENT_LIMITS.descriptionMax}
          isMultiline
          multiline
          numberOfLines={4}
          testID="event-description-input"
        />
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue} style={styles.counter}>
          {t('events.form.descriptionCounter', {
            count: values.description.length,
            max: EVENT_LIMITS.descriptionMax,
          })}
        </Text>
      </View>
    </FormSection>
  )
}

export function DatesSection({ values, errors, onChange }: SectionProps) {
  const { t } = useLanguage()
  const { rowStyle } = useSectionStyles()
  return (
    <FormSection title={t('events.form.sections.dates')}>
      <View style={rowStyle}>
        <DateTimeField
          label={t('events.form.startsAt')}
          value={values.startsAt}
          onChange={(v) => onChange('startsAt', v)}
          error={errors.startsAt}
          testID="event-starts-input"
        />
        <DateTimeField
          label={t('events.form.endsAt')}
          value={values.endsAt}
          onChange={(v) => onChange('endsAt', v)}
          error={errors.endsAt}
          testID="event-ends-input"
        />
      </View>
    </FormSection>
  )
}

export function PlaceSection({ values, errors, onChange }: SectionProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const { styles, rowStyle, grow } = useSectionStyles()
  return (
    <FormSection title={t('events.form.sections.place')} raised>
      <View style={styles.field}>
        <Text type="bodySmallBold" color={theme.colors.secondary}>
          {t('events.form.format')}
        </Text>
        <Switch
          left={{ label: t('events.format.in_person'), value: EVENT_FORMATS[0], icon: 'mapPin' }}
          middle={{ label: t('events.format.online'), value: EVENT_FORMATS[1] }}
          right={{ label: t('events.format.hybrid'), value: EVENT_FORMATS[2] }}
          value={values.format}
          onChange={(v) => onChange('format', v as EventFormState['format'])}
        />
      </View>
      {formNeedsVenue(values.format) ? (
        <VenueSearchField values={values} error={errors.location} onChange={onChange} />
      ) : null}
      {formNeedsOnlineUrl(values.format) ? (
        <TextInput
          title={t('events.form.onlineUrl')}
          placeholder={t('events.form.onlineUrlPlaceholder')}
          value={values.onlineUrl}
          onChangeText={(v) => onChange('onlineUrl', v)}
          autoCapitalize="none"
          maxLength={EVENT_LIMITS.urlMax}
          errorMessage={errors.onlineUrl}
          testID="event-online-url-input"
        />
      ) : null}
    </FormSection>
  )
}

export function VisibilitySection({ values, onChange }: SectionProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  return (
    <FormSection title={t('events.form.sections.visibility')}>
      <OptionChips
        label={t('events.form.visibility')}
        testID="event-visibility"
        options={EVENT_VISIBILITIES.map((value) => ({
          value,
          label: t(`events.form.visibilityOptions.${value}`),
        }))}
        value={values.visibility}
        // Tapping the selected chip would clear it: a choice is always kept here.
        onChange={(v) => v && onChange('visibility', v)}
      />
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue} testID="event-visibility-hint">
        {t(`events.form.visibilityHint.${values.visibility}`)}
      </Text>
    </FormSection>
  )
}

export function RegistrationSection({ values, errors, onChange }: SectionProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const { styles, rowStyle, grow } = useSectionStyles()
  return (
    <FormSection title={t('events.form.sections.registration')}>
      <View style={rowStyle}>
        <View style={[styles.field, grow()]}>
          <TextInput
            title={t('events.form.capacity')}
            placeholder="120"
            value={values.capacity}
            onChangeText={(v) => onChange('capacity', v)}
            keyboardType="number-pad"
            errorMessage={errors.capacity}
            testID="event-capacity-input"
          />
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
            {t('events.form.capacityHint')}
          </Text>
        </View>
        <DateTimeField
          label={t('events.form.registrationClosesAt')}
          value={values.registrationClosesAt}
          onChange={(v) => onChange('registrationClosesAt', v)}
          error={errors.registrationClosesAt}
          hint={t('events.form.registrationClosesAtHint')}
          testID="event-deadline-input"
        />
      </View>
    </FormSection>
  )
}

export function TicketingSection({ values, errors, onChange, billing }: SectionProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const { styles } = useSectionStyles()
  const cents = values.isPaid ? parsePriceInput(values.price) : null
  return (
    <FormSection title={t('events.form.sections.ticketing')}>
      <OptionChips
        label={t('events.form.ticketing')}
        testID="event-ticketing"
        options={[
          { value: 'free', label: t('events.price.free') },
          { value: 'paid', label: t('events.price.paid') },
        ]}
        value={values.isPaid ? 'paid' : 'free'}
        // Tapping the selected chip would clear it: a choice is always kept here.
        onChange={(v) => v && onChange('isPaid', v === 'paid')}
      />
      {values.isPaid ? (
        <View style={styles.field}>
          <TextInput
            title={t('events.form.price')}
            placeholder="25"
            value={values.price}
            onChangeText={(v) => onChange('price', v)}
            keyboardType="decimal-pad"
            errorMessage={errors.price}
            testID="event-price-input"
          />
          <Text type="bodySmallBold" color={theme.colors.primary} testID="event-price-preview">
            {cents !== null && cents >= EVENT_PRICE_MIN_CENTS && cents <= EVENT_PRICE_MAX_CENTS
              ? t('events.form.pricePreview', {
                  price: formatEventPrice(cents, currentLanguage),
                })
              : ' '}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
            {t('events.form.priceHint', {
              min: formatEventPrice(EVENT_PRICE_MIN_CENTS, currentLanguage),
              max: formatEventPrice(EVENT_PRICE_MAX_CENTS, currentLanguage),
            })}
          </Text>
        </View>
      ) : null}
      {values.isPaid && billing ? (
        <View style={styles.readonlyFee} testID="commission-readonly">
          <Text type="bodySmallBold" color={theme.colors.secondary}>
            {billing.commissionRateBps > 0
              ? t('events.form.commissionReadonly', {
                  rate: formatBps(billing.commissionRateBps, currentLanguage),
                  who: t(`events.form.feeByLower.${billing.feePaidBy}`),
                })
              : t('events.form.commissionNone')}
          </Text>
        </View>
      ) : null}
    </FormSection>
  )
}

export function CoverSection({ values, onChange }: SectionProps) {
  const { t } = useLanguage()
  return (
    <FormSection title={t('events.form.sections.cover')}>
      <CoverImageField
        value={values.coverImageUrl}
        onChange={(url) => onChange('coverImageUrl', url)}
      />
    </FormSection>
  )
}

export function ContactSection({ values, errors, onChange }: SectionProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const { styles, rowStyle, grow } = useSectionStyles()
  return (
    <FormSection title={t('events.form.sections.contact')}>
      <View style={rowStyle}>
        <View style={[styles.field, grow()]}>
          <TextInput
            title={t('events.form.contactEmail')}
            placeholder={t('events.form.contactEmailPlaceholder')}
            value={values.contactEmail}
            onChangeText={(v) => onChange('contactEmail', v)}
            keyboardType="email-address"
            autoCapitalize="none"
            maxLength={EVENT_LIMITS.emailMax}
            errorMessage={errors.contactEmail}
            testID="event-contact-email-input"
          />
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
            {t('events.form.contactEmailHint')}
          </Text>
        </View>
        <View style={[styles.field, grow()]}>
          <TextInput
            title={t('events.form.websiteUrl')}
            placeholder={t('events.form.websiteUrlPlaceholder')}
            value={values.websiteUrl}
            onChangeText={(v) => onChange('websiteUrl', v)}
            autoCapitalize="none"
            maxLength={EVENT_LIMITS.urlMax}
            errorMessage={errors.websiteUrl}
            testID="event-website-input"
          />
        </View>
      </View>
    </FormSection>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    row: { flexDirection: 'row', gap: theme.spacing.lg },
    column: { flexDirection: 'column' },
    field: { gap: theme.spacing.xs },
    flex1: { flex: 1 },
    flex2: { flex: 2 },
    counter: { alignSelf: 'flex-end' },
    readonlyFee: {
      backgroundColor: theme.colors.sidebar,
      borderRadius: theme.border.medium,
      padding: theme.spacing.md,
    },
  })
